'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'
import { useFormikContext } from 'formik'

// lib
import {
  accessibilityLabel,
  accessibilityRole,
  distribute
} from '../../_support'
import { useField, UseFieldOptions } from '../field'
import { CheckBoxInput, CheckBoxInputProps } from '../inputs'
import CheckBoxWrapper from '../inputs/checkbox-input/checkbox-wrapper'
import Box, { BoxProps } from '../../box'
import Pressable from '../../pressable'
import Text, { TextProps } from '../../text'

/* -----------------------------------------------------------------------------
 * CheckBoxField
 * -------------------------------------------------------------------------- */

export type CheckBoxFieldProps = Omit<BoxProps, 'height' | 'width'> &
  TextProps &
  UseFieldOptions &
  CheckBoxInputProps & {
    size?: number
    label?: string
  }

const CheckBoxField = <FormValues extends Record<string, any>>({
  label,
  name,
  disabled,
  onValueChange,
  size = 20,
  ...rest
}: CheckBoxFieldProps) => {
  const { inputProps, containerProps } = useField({
    name,
    ...rest
  })
  const [boxProps, textProps] = distribute(rest, {
    using: Box.styleKeys,
    to: {},
    rest: {}
  })
  const { values, setFieldTouched, setFieldValue } =
    useFormikContext<FormValues>()
  const isChecked = values[name]
  const wrapperProps = (
    isChecked ? { variant: 'violetGradient' } : { bg: 'white' }
  ) as any

  const handleToggle = (newVal: boolean) => {
    setFieldTouched(name)
    setFieldValue(name, newVal)
    onValueChange(newVal)
  }

  return (
    <Pressable
      onPress={() => handleToggle(!isChecked)}
      disabled={disabled}
      {...containerProps}
    >
      <Box
        flex='1 1 auto'
        flexDirection='row'
        alignItems='center'
        pointerEvents='none'
      >
        <CheckBoxWrapper
          isChecked={isChecked}
          size={size}
          {...wrapperProps}
          {...boxProps}
        >
          <CheckBoxInput
            tintColor='transparent'
            tintColors={{ true: 'transparent', false: 'transparent' }}
            onCheckColor='transparent'
            onFillColor='transparent'
            onTintColor='transparent'
            onAnimationType='fade'
            offAnimationType='fade'
            height={size}
            width={size}
            style={{ background: 'transparent', borderWidth: 0 }}
            accessibilityRole={accessibilityRole('checkbox')}
            {...accessibilityLabel(`${name} Input`)}
            {...inputProps}
          />
        </CheckBoxWrapper>
        {label && (
          <Text
            accessibilityRole={accessibilityRole('label')}
            ml='08'
            {...textProps}
          >
            {label}
          </Text>
        )}
      </Box>
    </Pressable>
  )
}

export default CheckBoxField
